import * as React from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import useTheme from '@mui/material/styles/useTheme'

import { addLeadFieldNameLabels } from '@user/components/Settings/EntityFormSettings/constants'
import { IEntityFormSetting } from '@user/store/entityFormSettings/typings'

import { getContentStyles } from './styling'

type Props = {
  onChangeRequired: () => void
  required: boolean
  systemNonOptionalRequired: boolean
  settingItem: IEntityFormSetting
  hideRequired?: boolean
  isTidalThemeActive: boolean
}

/**
 * Renders a card's label, description, and active/required toggles.
 *
 * One tree covers both themes: the container's layout, the label's variant and
 * colour, and the "required" row's colour all come from `getContentStyles`.
 * The Tidal container is a flex column and the legacy one a plain block with
 * outer margins, which is a difference in the container's own `sx` rather than
 * in the element tree — both render a single wrapper element.
 */
export function FieldSettingsCardContent(props: Props) {
  const {
    onChangeRequired,
    required,
    systemNonOptionalRequired,
    settingItem,
    hideRequired = false,
    isTidalThemeActive,
  } = props
  const theme = useTheme()
  const styles = getContentStyles(theme, isTidalThemeActive)

  const requiredContent =
    !hideRequired &&
    (systemNonOptionalRequired ? (
      <Typography variant="body2" sx={styles.requiredText}>
        Field required by system
      </Typography>
    ) : (
      <Stack direction="row">
        <Switch
          size="small"
          checked={required}
          onChange={onChangeRequired}
          onClick={(evt) => evt.stopPropagation()}
        />
        <Typography variant="body2" sx={styles.requiredText}>
          Make field required
        </Typography>
      </Stack>
    ))

  return (
    <Box sx={styles.container}>
      <Typography variant={styles.props.labelVariant} sx={styles.label}>
        {addLeadFieldNameLabels[settingItem.fieldId]}
      </Typography>
      {requiredContent}
    </Box>
  )
}
FieldSettingsCardContent.displayName = 'FieldSettingsCardContent'
